import { constructMetadata, SeoData } from '@carvana/traffic';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type React from 'react';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import RouteContext from '~/contexts/RouteContext';
import { useUserLocation } from '~/contexts/UserContext';
import type { VanityUrlWithPath } from '~/contracts';
import getGeolocationInfo from '~/utilities/getGeolocationInfo';

type ContextType = {
  seoData: SeoData;
  setChanged: () => void;
  vanityUrl?: VanityUrlWithPath;
};

const defaultContext: ContextType = {
  seoData: {
    flags: {
      hasCvnaid: false,
      isAllModels: false,
      isMultiMake: false,
      isNoMake: false,
      isSingleModel: false
    },
    seoH1: ''
  },
  setChanged() {},
  vanityUrl: undefined
};

const SEOContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  vanityUrl?: VanityUrlWithPath;
}>;

export function SEOContextProvider(props: OwnProps) {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { currentUrl } = useContext(RouteContext);
  const userLocation = useUserLocation();

  const [didChange, setDidChange] = useState(false);
  const setChanged = useCallback(() => {
    setDidChange(true);
  }, []);

  const seoData = useMemo(
    () =>
      constructMetadata(
        appliedFilters,
        getGeolocationInfo(currentUrl, userLocation)
      ),
    [appliedFilters, currentUrl, userLocation]
  );

  const value = useMemo<ContextType>(
    () => ({
      seoData,
      setChanged,
      vanityUrl: didChange ? undefined : props.vanityUrl
    }),
    [didChange, props.vanityUrl, seoData, setChanged]
  );

  return (
    <SEOContext.Provider value={value}>{props.children}</SEOContext.Provider>
  );
}

SEOContextProvider.displayName = 'SEOContextProvider';

export default SEOContext;
